import { HttpBody, HttpOptions, HttpResponse, IHttpClient } from '@infra/http/IHttpClient'
import axios, { AxiosResponse } from 'axios'
import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { SessionExpired } from '@/domain/events/SessionExpired'
import { AuthState } from '@/domain/states/AuthState'

import { readAuthCookie } from './authCookie'
import { httpAgent } from './axiosInstance'
import { HttpTransportError } from './HttpTransportError'

import { isRecord } from '@/utils/safe'
import { classifyHttpFailure } from '@/utils/withNetworkRetry'
import { withNetworkRetry } from '@/utils/withNetworkRetry'

/**
 * Orçamento de retentativas para falha de TRANSPORTE (flap de rede, timeout).
 *
 * Fica aqui, e não espalhado por repositório, porque conserta os ~19 repositórios, o boot e as
 * chamadas fire-and-forget de uma vez. Três tentativas com jitter cobrem uma janela de ~1s, que é a
 * duração típica de um evento do `NetworkChangeNotifier` do Chrome.
 *
 * Regra de convivência com o resto do app: **transporte retenta aqui; ninguém mais retenta erro já
 * marcado `isTransient`.** Empilhar dois mecanismos multiplica requisições e piora o problema.
 */
const TRANSIENT_RETRY = { retries: 2, baseDelayMs: 250, maxDelayMs: 1000 }

/**
 * Mensagem do `403` com que a API do ecossistema recusa a requisição por falta de token válido — é a
 * mensagem padrão do guard do Nest. Medido na API em 20/10/2026: header `Authorization` ausente ou fora
 * do formato `Token <x>` responde `403 Forbidden resource`; token inválido responde `401`. Os `403` de
 * recurso trazem uma chave própria (`notUsersAircraft`, `notUsersChecklist`...) e não são sessão.
 */
const SESSION_FORBIDDEN_MESSAGE = 'Forbidden resource'

@injectable()
export class AxiosHttpClient implements IHttpClient {
  constructor(@inject(InjectionTokens.AuthState) private authState: AuthState) {}

  async get<T>(url: string, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>((requestOptions) => httpAgent.get<T>(url, requestOptions), options, true)
  }

  async post<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.post<T>(url, body, requestOptions),
      options,
      options?.idempotent === true
    )
  }

  async put<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.put(url, body, requestOptions),
      options,
      options?.idempotent === true
    )
  }

  async delete<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.delete(url, { ...requestOptions, data: body }),
      options,
      options?.idempotent === true
    )
  }

  // PRIVATE METHODS

  /**
   * Executa a requisição e, num 401 (ou 403 de token recusado), tenta recuperar a sessão uma única vez.
   *
   * O token vive em `AuthState`, preenchido a partir do cookie `_nat` no boot. Como o cookie é de
   * domínio compartilhado com o Account e os outros apps da NexAtlas, um login/logout feito em outra aba
   * o rotaciona enquanto esta aba está aberta — a aba seguia usando o token morto e toda escrita passava
   * a falhar com 401 até um reload, sem nenhuma pista para o usuário. Aqui a gente relê o cookie: se ele
   * mudou, atualiza o estado e repete a requisição com o token novo; se não mudou, a sessão realmente
   * acabou e o evento avisa quem trata a UX (Authenticator).
   */
  private async _send<T>(
    perform: (options: HttpOptions & { validateStatus: () => boolean }) => Promise<AxiosResponse<T>>,
    options?: HttpOptions,
    allowTransientRetry = false,
    isSessionRetry = false
  ): Promise<HttpResponse<T>> {
    // `idempotent` é nosso, não do axios: não deve viajar na config da requisição.
    const { idempotent: _idempotent, ...axiosOptions } = options ?? {}

    try {
      const requestOptions = { ...axiosOptions, validateStatus: () => true }

      // `validateStatus: () => true` faz com que NENHUM status HTTP rejeite. Logo `perform` só
      // rejeita por falha de transporte — que é exatamente o que vale retentar. Um 5xx é resposta
      // do servidor e passa direto, sem retry (decisão de quem chama).
      const axiosResponse = allowTransientRetry
        ? await withNetworkRetry(() => perform(requestOptions), TRANSIENT_RETRY)
        : await perform(requestOptions)

      // O 403 do DigitalOcean Spaces (manifest de cartas ausente) também não chega aqui como sessão:
      // `_recoverSession` só age quando a requisição enviou `Authorization`, e essas chamadas não enviam.
      if (this._isSessionFailure(axiosResponse) && !isSessionRetry) {
        const recoveredOptions = this._recoverSession(options)
        if (recoveredOptions) {
          return this._send<T>(perform, recoveredOptions, allowTransientRetry, true)
        }
      }

      const plainHeaders = Object.fromEntries(axiosResponse.headers as any)

      return {
        success: true,
        status: axiosResponse.status,
        headers: plainHeaders,
        response: axiosResponse.data
      }
    } catch (error: any) {
      const classification = classifyHttpFailure(error)

      return {
        success: false,
        status: error?.response?.status,
        error: new HttpTransportError(error?.message ?? 'Network error', {
          code: error?.code,
          httpStatus: error?.response?.status,
          originalError: error
        }),
        code: error?.code,
        isTransient: classification === 'transient',
        isCanceled: classification === 'canceled'
      }
    }
  }

  /**
   * Diz se a resposta pode significar sessão morta. Todo `401` pode; um `403` só quando traz a mensagem de
   * token recusado. Um `403` de recurso (ex.: salvar checklist de aeronave apagada) é erro de quem chamou:
   * tratá-lo como sessão abria o modal "Sua sessão expirou" com a sessão válida.
   */
  private _isSessionFailure(axiosResponse: AxiosResponse<unknown>): boolean {
    if (axiosResponse.status === 401) return true
    if (axiosResponse.status !== 403) return false

    const body = axiosResponse.data
    return isRecord(body) && body.message === SESSION_FORBIDDEN_MESSAGE
  }

  /**
   * Devolve as options com o token novo quando o cookie tem um token diferente do que foi enviado.
   * Devolve `null` quando não há como recuperar — aí emite `SessionExpired`.
   */
  private _recoverSession(options?: HttpOptions): HttpOptions | null {
    const sentAuthorization = this._getAuthorizationHeader(options)
    // Requisição sem Authorization (ex.: serviço de terceiro) não é problema de sessão nossa.
    if (!sentAuthorization) return null

    const cookieToken = readAuthCookie()
    if (!cookieToken) {
      SessionExpired.next()
      return null
    }

    const freshAuthorization = `Token ${cookieToken}`
    if (freshAuthorization === sentAuthorization) {
      // O cookie continua com o mesmo token que o servidor acabou de rejeitar: sessão encerrada.
      SessionExpired.next()
      return null
    }

    if (cookieToken !== this.authState.getStateSnapshot().token) {
      this.authState.setToken(cookieToken)
    }

    const headers = { ...(options?.headers ?? {}) } as Record<string, string>
    const headerKey = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization') ?? 'Authorization'
    headers[headerKey] = freshAuthorization

    return { ...options, headers }
  }

  private _getAuthorizationHeader(options?: HttpOptions): string | null {
    const headers = (options?.headers ?? {}) as Record<string, unknown>
    const headerKey = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization')
    if (!headerKey) return null

    const value = headers[headerKey]
    return typeof value === 'string' ? value : null
  }
}
